// src/admin/pages/Checkout.tsx
import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { CreditCard, Phone, Building2, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ordersApi } from '../utils/api';

type PaymentMethod = 'mpesa' | 'airtel' | 'card' | 'bank';

export const Checkout = () => {
  const { state, dispatch } = useApp();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('mpesa');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    notes: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Helper function to get full image URL
  const getFullImageURL = (url: string): string => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${import.meta.env.VITE_API_BASE_IMAGES_URL}${url}`;
  };

  useEffect(() => {
    // Check if there's a buyNowItem in sessionStorage
    const buyNowItem = sessionStorage.getItem('buyNowItem');
    if (buyNowItem) {
      const parsedItem = JSON.parse(buyNowItem);
      // Set the cart to contain only the buyNowItem
      dispatch({ type: 'SET_CART', payload: [parsedItem] });
      // Optionally, remove the item from sessionStorage after setting the cart
      sessionStorage.removeItem('buyNowItem');
    }
  }, [dispatch]);

  const subtotal = state.cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = 0; // Standard shipping fee
  const total = subtotal + shipping;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.address || !formData.city) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      setIsLoading(true);
      const response = await ordersApi.create({
        state,
        formData,
        paymentMethod,
      });

      if (response.data) {
        // Clear buyNowItem from sessionStorage if present
        sessionStorage.removeItem('buyNowItem');
        // Redirect to Pesapal payment URL
        if (response.data.paymentURL) {
          window.location.href = response.data.paymentURL;
        } else {
          toast.error('Payment URL not received. Please try again.');
        }
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || 'An error occurred while placing your order.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Checkout Form */}
          <div className="space-y-8">
            {/* Personal Information */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4">Personal Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                    required
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  required
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  required
                />
              </div>
            </div>

            {/* Shipping Information */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4">Shipping Information</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  required
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  required
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">Order Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                ></textarea>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4">Payment Method</h2>
              <div className="space-y-4">
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50">
                  <input
                    type="radio"
                    name="payment"
                    checked={paymentMethod === 'mpesa'}
                    onChange={() => setPaymentMethod('mpesa')}
                    className="text-pink-500 focus:ring-pink-500"
                    required
                  />
                  <Phone className="h-6 w-6 text-green-500 ml-3" />
                  <span className="ml-3">M-Pesa</span>
                </label>
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50">
                  <input
                    type="radio"
                    name="payment"
                    checked={paymentMethod === 'airtel'}
                    onChange={() => setPaymentMethod('airtel')}
                    className="text-pink-500 focus:ring-pink-500"
                    required
                  />
                  <Phone className="h-6 w-6 text-red-500 ml-3" />
                  <span className="ml-3">Airtel Money</span>
                </label>
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50">
                  <input
                    type="radio"
                    name="payment"
                    checked={paymentMethod === 'card'}
                    onChange={() => setPaymentMethod('card')}
                    className="text-pink-500 focus:ring-pink-500"
                    required
                  />
                  <CreditCard className="h-6 w-6 text-blue-500 ml-3" />
                  <span className="ml-3">Credit/Debit Card</span>
                </label>
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50">
                  <input
                    type="radio"
                    name="payment"
                    checked={paymentMethod === 'bank'}
                    onChange={() => setPaymentMethod('bank')}
                    className="text-pink-500 focus:ring-pink-500"
                    required
                  />
                  <Building2 className="h-6 w-6 text-gray-500 ml-3" />
                  <span className="ml-3">Bank Transfer</span>
                </label>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white p-6 rounded-lg shadow-md sticky top-24">
              <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
              <div className="space-y-4">
                {state.cart.map((item) => (
                  <div key={item.id} className="flex items-center gap-4">
                    <img
                      src={
                        item.images && item.images.length > 0
                          ? getFullImageURL(item.images[0].url)
                          : '/path/to/default-image.jpg'
                      }
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-md"
                      onError={(e) => {
                        e.currentTarget.src = '/path/to/default-image.jpg';
                        e.currentTarget.onerror = null;
                      }}
                    />
                    <div className="flex-1">
                      <h3 className="font-medium">{item.name}</h3>
                      <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                      {item.selectedVariations && (
                        <p className="text-sm text-gray-500">
                          {Object.entries(item.selectedVariations)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(', ')}
                        </p>
                      )}
                    </div>
                    <p className="font-medium">KES {(item.price * item.quantity).toLocaleString()}</p>
                  </div>
                ))}

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>KES {subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span>KES {shipping.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span>KES {total.toLocaleString()}</span>
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  className="w-full bg-pink-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-pink-600 transition-colors mt-6 flex items-center justify-center gap-2"
                >
                  <Wallet className="h-5 w-5" />
                  Place Order
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
